import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Cookie-based auth: the JWT lives in a secure httpOnly cookie set by the backend,
// so it is never exposed to JavaScript (XSS-safe). Always send credentials.
axios.defaults.withCredentials = true;

let authed = false;
export const markAuthed = (v) => { authed = !!v; };

axios.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err.response?.status === 401 && authed) {
      authed = false;
      if (!window.location.pathname.includes("login")) window.location.reload();
    }
    return Promise.reject(err);
  }
);

export const authApi = {
  me: () => axios.get(`${API}/auth/me`).then((r) => r.data),
  register: (email, name) => axios.post(`${API}/auth/register`, { email, name }).then((r) => r.data),
  requestOtp: (email) => axios.post(`${API}/auth/request-otp`, { email }).then((r) => r.data),
  verifyOtp: (email, code) => axios.post(`${API}/auth/verify-otp`, { email, code }).then((r) => r.data),
  logout: () => axios.post(`${API}/auth/logout`).then((r) => r.data),
  pending: () => axios.get(`${API}/auth/pending`).then((r) => r.data),
  users: () => axios.get(`${API}/auth/users`).then((r) => r.data),
  approve: (id) => axios.post(`${API}/auth/approve/${id}`).then((r) => r.data),
  reject: (id) => axios.post(`${API}/auth/reject/${id}`).then((r) => r.data),
};

export const api = {
  platforms: () => axios.get(`${API}/platforms`).then((r) => r.data),
  resources: () => axios.get(`${API}/resources`).then((r) => r.data),
  tools: () => axios.get(`${API}/tools`).then((r) => r.data),
  syncDocs: () => axios.post(`${API}/sync-docs`).then((r) => r.data),
  symbols: () => axios.get(`${API}/symbols`).then((r) => r.data),
  addSymbol: (s) => axios.post(`${API}/symbols`, s).then((r) => r.data),
  importSymbols: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return axios.post(`${API}/symbols/import`, fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },
  deleteSymbol: (id) => axios.delete(`${API}/symbols/${id}`).then((r) => r.data),
  parseLegal: (text) => axios.post(`${API}/lot/parse-legal`, { text }).then((r) => r.data),
  recomputeLot: (courses) => axios.post(`${API}/lot/recompute`, { courses }).then((r) => r.data),
  lotReportPdf: (payload) => axios.post(`${API}/lot/report.pdf`, payload, { responseType: "blob" }).then((r) => r.data),
  readPlat: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return axios.post(`${API}/lot/read-plat`, fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },
  clientCommands: () => axios.get(`${API}/client-commands`).then((r) => r.data),  batchConvert: (payload) => axios.post(`${API}/batch-convert`, payload).then((r) => r.data),
  convertAll: (payload) => axios.post(`${API}/convert-all`, payload).then((r) => r.data),
  projects: () => axios.get(`${API}/projects`).then((r) => r.data),
  getProject: (id) => axios.get(`${API}/projects/${id}`).then((r) => r.data),
  saveProject: (name, generation_id) => axios.post(`${API}/projects`, { name, generation_id }).then((r) => r.data),
  deleteProject: (id) => axios.delete(`${API}/projects/${id}`).then((r) => r.data),
  manuals: (clientId) => axios.get(`${API}/manuals${clientId ? `?client_id=${clientId}` : ""}`).then((r) => r.data),
  upload: (file, category, clientId) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("category", category);
    if (clientId) fd.append("client_id", clientId);
    return axios
      .post(`${API}/manuals/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } })
      .then((r) => r.data);
  },
  clients: () => axios.get(`${API}/clients`).then((r) => r.data),
  createClient: (name) => axios.post(`${API}/clients`, { name }).then((r) => r.data),
  updateClient: (id, patch) => axios.patch(`${API}/clients/${id}`, patch).then((r) => r.data),
  presetLibrary: () => axios.get(`${API}/preset-library`).then((r) => r.data),
  savePresetLibrary: (presets) => axios.put(`${API}/preset-library`, { presets }).then((r) => r.data),
  deleteClient: (id) => axios.delete(`${API}/clients/${id}`).then((r) => r.data),
  clientSummary: (id) => axios.get(`${API}/clients/${id}/summary`).then((r) => r.data),
  fieldAnalysis: ({ text, clientId, file }) => {
    const fd = new FormData();
    if (text) fd.append("text", text);
    if (clientId) fd.append("client_id", clientId);
    if (file) fd.append("file", file);
    return axios.post(`${API}/field-analysis`, fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },
  draftingSuggestions: (clientId) => axios.post(`${API}/drafting-suggestions`, { client_id: clientId }).then((r) => r.data),
  grounding: (clientId) => axios.get(`${API}/grounding`, { params: { client_id: clientId } }).then((r) => r.data),
  importGeneration: (payload) => axios.post(`${API}/generations/import`, payload).then((r) => r.data),
  validateLisp: (payload) => axios.post(`${API}/validate-lisp`, payload).then((r) => r.data),
  clientExportUrl: (id) => `${API}/clients/${id}/export.zip`,
  deleteManual: (id) => axios.delete(`${API}/manuals/${id}`).then((r) => r.data),
  updateManual: (id, patch) => axios.patch(`${API}/manuals/${id}`, patch).then((r) => r.data),
  baseCommands: () => axios.get(`${API}/base-commands`).then((r) => r.data),
  enrichBaseCommands: () => axios.post(`${API}/base-commands/enrich`).then((r) => r.data),
  renameClash: (id, opts) => axios.post(`${API}/generations/${id}/rename-clash`, opts || {}).then((r) => r.data),
  generation: (id) => axios.get(`${API}/generations/${id}`).then((r) => r.data),
  generate: (payload) => axios.post(`${API}/generate`, payload).then((r) => r.data),
  generationStatus: (jobId) => axios.get(`${API}/generate/status/${jobId}`).then((r) => r.data),
  cancelJob: (jobId) => axios.post(`${API}/jobs/${jobId}/cancel`).then((r) => r.data),
  parsePoints: (file, order) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("order", order || "PNEZD");
    return axios.post(`${API}/points/parse`, fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },
  debugLisp: (payload) => axios.post(`${API}/debug-lisp`, payload).then((r) => r.data),
  debugBatch: (payload) => axios.post(`${API}/debug-batch`, payload).then((r) => r.data),
  debugBatchZipUrl: (jobId) => `${API}/debug-batch/${jobId}/zip`,
  profiles: () => axios.get(`${API}/profiles`).then((r) => r.data),
  saveProfile: (payload) => axios.post(`${API}/profiles`, payload).then((r) => r.data),
  deleteProfile: (id) => axios.delete(`${API}/profiles/${id}`).then((r) => r.data),
  inspectTemplate: (manual_id) => axios.post(`${API}/template/inspect`, { manual_id }).then((r) => r.data),
  crossConvert: (payload) => axios.post(`${API}/cross-convert`, payload).then((r) => r.data),
  history: (clientId) => axios.get(`${API}/generations${clientId ? `?client_id=${clientId}` : ""}`).then((r) => r.data),
  downloadUrl: (id, variant) => `${API}/generations/${id}/download${variant ? `?variant=${variant}` : ""}`,
  manualUrl: (id) => `${API}/generations/${id}/manual.pdf`,
  lispCommands: (clientId) => axios.get(`${API}/clients/${clientId}/lisp-commands`).then((r) => r.data),
  portLisp: (clientId, payload) => axios.post(`${API}/clients/${clientId}/port-lisp`, payload).then((r) => r.data),
  portLispStatus: (jobId) => axios.get(`${API}/port-lisp/${jobId}`).then((r) => r.data),
  portLispZip: (jobId) => axios.get(`${API}/port-lisp/${jobId}/zip`, { responseType: "blob" }).then((r) => r.data),
  libraryLisp: () => axios.get(`${API}/library/lisp`).then((r) => r.data),
  uploadLibraryLisp: (fileList) => {
    const fd = new FormData();
    Array.from(fileList).forEach((f) => fd.append("files", f));
    return axios.post(`${API}/library/lisp`, fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },
  deleteLibraryLisp: (id) => axios.delete(`${API}/library/lisp/${id}`).then((r) => r.data),
  libraryPort: (payload) => axios.post(`${API}/library/port`, payload).then((r) => r.data),
  setLibraryCategory: (id, category) => axios.patch(`${API}/library/lisp/${id}`, { category }).then((r) => r.data),
  publishPreset: (payload) => axios.post(`${API}/library/publish`, payload).then((r) => r.data),
  onePagerPdf: (clientId) => axios.get(`${API}/clients/${clientId}/one-pager.pdf`, { responseType: "blob" }).then((r) => r.data),
  cogoInverse: (p1, p2) => axios.post(`${API}/cogo/inverse`, { p1, p2 }).then((r) => r.data),
  cogoTraverse: (start, courses) => axios.post(`${API}/cogo/traverse`, { start, courses }).then((r) => r.data),
  cogoIntersect: (payload) => axios.post(`${API}/cogo/intersect`, payload).then((r) => r.data),
  cogoPoints: () => axios.get(`${API}/cogo/points`).then((r) => r.data),
  saveCogoPoint: (p) => axios.post(`${API}/cogo/points`, p).then((r) => r.data),
  deleteCogoPoint: (id) => axios.delete(`${API}/cogo/points/${id}`).then((r) => r.data),
  importCogoPoints: (file, order) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("order", order || "PNEZD");
    return axios.post(`${API}/cogo/points/import`, fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },
  exportCogoPoints: (order) => axios.get(`${API}/cogo/points/export.csv`, { params: { order: order || "PNEZD" }, responseType: "blob" }).then((r) => r.data),
  cogoPointsMap: (startId, endId) => axios.post(`${API}/cogo/points/map`, { start_id: startId || null, end_id: endId || null }).then((r) => r.data),
  aiKeyStatus: () => axios.get(`${API}/ai-key/status`).then((r) => r.data),
  cogoPointsLisp: (payload) => axios.post(`${API}/cogo/points/plant.lsp`, payload || {}, { responseType: "blob" }).then((r) => r.data),
  profileFromPoints: (startId, endId) => axios.post(`${API}/profile/from-points`, { start_id: startId, end_id: endId }).then((r) => r.data),
  deliverablesPack: (payload) => axios.post(`${API}/deliverables/pack`, payload, { responseType: "blob" }).then((r) => r.data),
  profilePreview: (payload) => axios.post(`${API}/profile/preview`, payload).then((r) => r.data),
  profileReportPdf: (payload) => axios.post(`${API}/profile/report.pdf`, payload, { responseType: "blob" }).then((r) => r.data),
  lotAdjustmentCsv: (payload) => axios.post(`${API}/lot/adjustment.csv`, payload, { responseType: "blob" }).then((r) => r.data),
};

export { API };
